import Button from '@/components/Button/Button';
import Header from '@/components/Header/Header';
import { useSTKContext } from '@/context/context';
import {
  getDinamicHeaders,
  getStoreFilteredModifiers,
  getStoreGroups,
  getStores,
} from '@/services/menu/menus.helper.service';
import { useVariant, useMenuModifiersGroup } from '@/services/modifiers/modifiers.service';
import { FilteredModifierValues, Modifier } from '@/types/menus.types';
import { Box, Chip } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FZStoreMenuModifiersDetails.module.css';
import { useActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import { useCurrentUser } from '@/services/user/user.service';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import { RowSelectionState } from '@tanstack/react-table';
import classNames from 'classnames';
import { UserRoles, UserTypes } from '@/types/auth.types';
import FZStoreModifierFilter from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/internal/FZStoreModifierFilter';

import ModalStores from '@/pages/Franchisee/Menus/StoreMenuDetails/internal/ModalStores.module';
import { EditPriceModifierDrawer } from '@/pages/Franchisee/Menus/EditPriceDrawer/EditPriceModifierDrawer';
import { ModifierPriceDeviationsDrawer } from '@/pages/Franchisee/Menus/PriceDeviationsDrawer/ModifierPriceDeviationsDrawer';
import FZStoreModifierItemTable from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/internal/FZStoreModifierItemTable';
import ModalLockedModifiers from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/internal/ModalLockedModifiers';

const DEFAULT_CATEGORY_NAME = 'Pizza Cheese';
const provideNewSelectedRows = (selectedRows: RowSelectionState | undefined, storeModifiers: Modifier[]) => {
  const selectedStores = new Set(Object.keys(selectedRows as RowSelectionState));
  const newSelectedRows: Modifier[] = [];
  selectedStores.forEach((index) => {
    newSelectedRows.push(storeModifiers[+index]);
  });
  return newSelectedRows;
};

const FZStoreMenuModifiersDetails = () => {
  const { t } = useTranslation();
  const { alias } = useFranchiseeAlias();
  const { storesAndGroups } = useSTKContext();
  const [stores, setStores] = useState<string[]>(getStores(storesAndGroups));
  const [storeGroups, setStoreGroups] = useState(getStoreGroups(storesAndGroups));
  const [variantId, setVariantId] = useState('');
  const [isEditPriceDrawerOpen, setIsEditPriceDrawerOpen] = useState<boolean>(false);
  const [priceDeviationsData, setPriceDeviationsData] = useState<{
    isPriceDeviationsDrawerOpen: boolean;
    modifierCategory: string;
    modifierKeys: {
      modifierId: string;
      slotId: string;
      modifierWeightId: string;
      variantId: string;
    }[];
    selectedGroupName: string;
    cameFromEditDrawer: boolean;
  }>({
    isPriceDeviationsDrawerOpen: false,
    modifierCategory: '',
    modifierKeys: [],
    selectedGroupName: '',
    cameFromEditDrawer: false,
  });
  const [storeModifiers, setStoreModifiers] = useState<Modifier[]>();
  const { data: dataUser } = useCurrentUser(true);
  const { data: activeGlobalFreezeData } = useActiveGlobalFreezes(true);
  console.log({ priceDeviationsData });

  const isFranchiseeUserEdit = dataUser?.data.type === UserTypes.FRANCHISEE && dataUser?.data.role === UserRoles.EDIT;
  const isUserViewOnlyAccess = dataUser?.data.role === UserRoles.VIEW;
  const freezeExists = activeGlobalFreezeData?.data && activeGlobalFreezeData?.data?.length > 0;
  const isFranchiseeEditAndFreeze = isFranchiseeUserEdit && freezeExists;

  const [filteredValues, setFilteredValues] = useState<FilteredModifierValues>({});
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [filterApplied, setFilterApplied] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalLockedOpen, setModalLockedOpen] = useState(false);
  const [categoryName, setCategoryName] = useState<string>(DEFAULT_CATEGORY_NAME);
  const [subCategories, setSubCategories] = useState<
    | {
        value: string;
        label: string;
      }[]
    | undefined
  >([]);

  const { data: variant, isLoading: isVariantLoading } = useVariant(!!variantId, stores, storeGroups, variantId);

  const {
    data: menuModifiersGroup = [],
    isLoading: isStoreLoading,
    isFetching,
    refetch: refetchModifiers,
  } = useMenuModifiersGroup(filterApplied, stores, storeGroups, categoryName);

  const [headerWidth, setHeaderWidth] = useState('');

  const changeHeaderWidth = (newWidth: string) => {
    setHeaderWidth(newWidth);
  };
  const applyFilter = (pFilteredValues: FilteredModifierValues) => {
    setFilteredValues(pFilteredValues);
    setFilterApplied(true);
    setSelectedRows({});
  };
  const handleEditPriceDrawerClose = () => {
    setIsEditPriceDrawerOpen(false);
  };

  const handlePriceDeviationsDrawerClose = () => {
    setPriceDeviationsData({ ...priceDeviationsData, isPriceDeviationsDrawerOpen: false });
  };

  const clearFilter = () => {
    setFilteredValues({});
    setCategoryName(categoryName);
  };

  const categories = [
    { value: 'Pizza Cheese', label: 'Pizza - Cheese', category: 'Pizza Cheese', subcategories: null },
    { value: 'Pizza Toppings', label: 'Pizza - Toppings', category: 'Pizza Topping', subcategories: null },
    { value: 'Appetizer Topping', label: 'Appetizer - Toppings', category: 'Appetizer Topping', subcategories: null },
    { value: 'Other', label: 'Other', category: 'Other', subcategories: null },
  ];

  const modifiers = useMemo(
    () => getStoreFilteredModifiers(menuModifiersGroup, filteredValues),
    [menuModifiersGroup, filteredValues],
  );
  const lockedModifiers = useMemo(() => modifiers.filter((modifier) => modifier.priceIsLocked), [modifiers]);

  const handleEditPriceDrawerOpen = useCallback(() => {
    const selectedModifiers = provideNewSelectedRows(selectedRows, modifiers);
    setStoreModifiers(selectedModifiers);
    setIsEditPriceDrawerOpen(true);
  }, [modifiers, selectedRows]);

  const handleCategory = (newCategory: string) => {
    setFilteredValues({});
    setCategoryName(newCategory);
  };

  useEffect(() => {
    setSelectedRows({});
  }, [categoryName]);

  return (
    <div className={styles.page} data-testid="fzStoreModifierMenu">
      <div>
        <div style={{ minWidth: 'fit-content' }}>
          <Header
            title="Menu"
            breadcrumb={{ label: t('breadcrumb.backToAllMenus'), to: '/modifiers' }}
            includeCalenderLink
          />
        </div>
        <div className={styles.chip}>
          <Chip
            data-testid="icon"
            key="storeCount"
            label={t('menu.storesLabel', { count: stores?.length ?? 1 })}
            color="default"
            variant="outlined"
            onClick={() => setModalOpen(!modalOpen)}
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              height: '28px',
              backgroundColor: 'white',
              color: '#266DCC',
              borderColor: '#266DCC',
              marginLeft: '160px',
              marginTop: '-135px',
            }}
          />
        </div>
      </div>
      <div className={classNames(styles.header, { [styles.withAlias]: alias })} style={{ minWidth: headerWidth }}>
        <div className={styles.headerLabelAndLegendContainer}>
          <span>{t('menu.filter.modifiers')}</span>
          {!isFranchiseeEditAndFreeze && !isUserViewOnlyAccess && (
            <Button onClick={handleEditPriceDrawerOpen} disabled={Object.keys(selectedRows).length < 1}>
              {t('menu.editModifierPriceButton')}
            </Button>
          )}
        </div>
      </div>
      <div className={styles.filterActions}>
        <FZStoreModifierFilter
          onApplyFilter={(pFilteredValues: FilteredModifierValues) => applyFilter(pFilteredValues)}
          onClearFilter={clearFilter}
          categories={categories}
          categoryName={categoryName}
          onCategoryChange={(newCategory: string) => handleCategory(newCategory)}
          subCategories={subCategories || []}
          isLoadingCategories={false}
          searching={isFetching}
        />
      </div>
      {filterApplied && !isStoreLoading && (
        <Box>
          <Box>
            {isStoreLoading || modifiers.length === 0 ? (
              <Box pl={{ sm: 5, md: 7 }} pt={{ sm: 2, md: 4 }} fontSize="2.0rem">
                <p> No modifiers match your search for these stores </p>
              </Box>
            ) : (
              <FZStoreModifierItemTable
                menuModifiers={modifiers}
                dynamicHeaders={getDinamicHeaders(storesAndGroups)}
                setPriceDeviationsData={setPriceDeviationsData}
                selectedRows={selectedRows}
                onRowSelectionChange={setSelectedRows}
                variant={variant}
                modifierCategory={categoryName} // pass the current categoryName as modifierCategory
                onHandleSetVariantId={(pVariantId: any) => setVariantId(pVariantId)}
                isLoading={isVariantLoading}
                changeHeaderSize={changeHeaderWidth}
                storesAndGroups={storesAndGroups}
                isFranchiseeEditAndFreeze={isFranchiseeEditAndFreeze}
                onShowLockedModal={() => setModalLockedOpen(true)}
                lockedModifiers={lockedModifiers}
              />
            )}
          </Box>
        </Box>
      )}

      {modalOpen && <ModalStores stores={stores} isOpen={modalOpen} onClose={() => setModalOpen(false)} />}
      {modalLockedOpen && (
        <ModalLockedModifiers
          lockedModifiers={lockedModifiers}
          isOpen={modalLockedOpen}
          onClose={() => setModalLockedOpen(false)}
        />
      )}

      {isEditPriceDrawerOpen && (
        <EditPriceModifierDrawer
          isOpen={isEditPriceDrawerOpen}
          onClose={handleEditPriceDrawerClose}
          storeModifiers={storeModifiers || []}
          afterSave={() => {
            setSelectedRows({});
            refetchModifiers();
          }}
          setPriceDeviationsData={setPriceDeviationsData}
          categoryName={categoryName}
        />
      )}
      {priceDeviationsData.isPriceDeviationsDrawerOpen && (
        <ModifierPriceDeviationsDrawer
          isOpen={priceDeviationsData.isPriceDeviationsDrawerOpen}
          onClose={handlePriceDeviationsDrawerClose}
          modifierCategory={priceDeviationsData.modifierCategory}
          modifierKeys={priceDeviationsData.modifierKeys}
          selectedGroupName={priceDeviationsData.selectedGroupName}
          cameFromEditDrawer={priceDeviationsData.cameFromEditDrawer}
        />
      )}
    </div>
  );
};
export default FZStoreMenuModifiersDetails;
